/**
 * Attack Sound System
 * Gets attack sound for units based on database field or fallback mapping
 * 
 * Sound types from database (orders_units.attack_sound_type):
 * - 'acid' - Acid attacks
 * - 'beam' - Beam weapons
 * - 'blunt' - Blunt force attacks
 * - 'cannon' - Cannon fire
 * - 'claws' - Claw attacks
 * - 'default' - Fallback sound
 * - 'deploy' - Deployment sound
 * - 'destroy' - Destruction sound
 * - 'energy' - Energy weapons
 * - 'energy beam' - Energy beam weapons
 * - 'energy dart' - Energy dart weapons
 * - 'energy sword' - Energy sword attacks
 * - 'energy whip' - Energy whip attacks
 * - 'explosion' - Explosion sounds
 * - 'explosives' - Explosive weapons
 * - 'fire' - Fire attacks
 * - 'gas' - Gas attacks
 * - 'grow' - Growth sounds
 * - 'heavy machine gun' - Heavy machine gun fire
 * - 'machine gun' - Machine gun fire
 * - 'pistol' - Pistol shots
 * - 'reposition' - Reposition sounds
 * - 'shadow' - Shadow attacks
 * - 'shockwave' - Shockwave attacks
 * - 'shotgun' - Shotgun blasts
 * - 'Monster' - Monster attack sound (monster.mp3)
 * - 'shrink' - Shrink sounds
 * - 'smoke' - Smoke effects
 * - 'sniper' - Sniper shots
 * - 'sparks' - Spark effects
 * - 'spray' - Spray attacks
 * - 'sword' - Sword attacks
 */

// Fallback mapping for units without attack_sound_type in database
// This is used as a backup if attack_sound_type is not set
const fallbackAttackSoundMapping = {
  // Add unit IDs here if needed as fallback
  // Example: 101: 'sniper',
};

/**
 * Get attack sound path for a unit
 * Priority: 1. Database field (attack_sound_type), 2. Fallback mapping, 3. Name inference, 4. Default
 * @param {Object} unit - Unit object with attack_sound_type, id, and name properties
 * @returns {string} - Path to attack sound file
 */
export function getAttackSound(unit) {
  if (!unit) {
    return '/sounds/blunt.mp3'; // Default fallback (blunt is the most common)
  }
  
  // Priority 1: Use database field if available (most reliable)
  if (unit.attack_sound_type) {
    return getSoundPathForType(unit.attack_sound_type);
  }
  
  // Priority 2: Fallback to hardcoded mapping
  if (unit.id && fallbackAttackSoundMapping[unit.id]) {
    return getSoundPathForType(fallbackAttackSoundMapping[unit.id]);
  }
  
  // Priority 3: Infer from name (less reliable, but useful for units without DB field set)
  if (unit.name) {
    const inferredType = inferSoundTypeFromName(unit.name);
    if (inferredType) {
      return getSoundPathForType(inferredType);
    }
  }
  
  // Priority 4: Default
  return '/sounds/blunt.mp3';
}

/**
 * Get sound file path for a sound type
 * Maps database attack_sound_type values to actual sound file paths
 * @param {string} soundType - Sound type from database
 * @returns {string} - Path to sound file
 */
function getSoundPathForType(soundType) {
  // Normalize sound type (lowercase, trim)
  const normalizedType = soundType ? soundType.toLowerCase().trim() : 'default';
  
  const soundPaths = {
    'acid': '/sounds/acid.mp3',
    'beam': '/sounds/beam.mp3',
    'blunt': '/sounds/blunt.mp3',
    'cannon': '/sounds/cannon.mp3', // Can use cannon_2.mp3 as alternative
    'claws': '/sounds/claw.mp3',
    'claw': '/sounds/claw.mp3', // Alias for claws
    'default': '/sounds/blunt.mp3',
    'deploy': '/sounds/deploy.mp3',
    'destroy': '/sounds/destroy.mp3',
    'energy': '/sounds/energy.mp3',
    'energy beam': '/sounds/energy_2.mp3',
    'energybeam': '/sounds/energy_2.mp3', // Alias without space
    'energy dart': '/sounds/darts.mp3',
    'energydart': '/sounds/darts.mp3', // Alias without space
    'energy sword': '/sounds/energy_sword.mp3',
    'energysword': '/sounds/energy_sword.mp3', // Alias without space
    'energy whip': '/sounds/energy_whip.mp3',
    'energywhip': '/sounds/energy_whip.mp3', // Alias without space
    'explosion': '/sounds/explosion.mp3',
    'explosives': '/sounds/explosive.mp3',
    'fire': '/sounds/fire.mp3',
    'gas': '/sounds/gas.mp3',
    'grow': '/sounds/grow.mp3',
    'heavy machine gun': '/sounds/heavy_machine_gun.mp3',
    'heavymachinegun': '/sounds/heavy_machine_gun.mp3', // Alias without space
    'machine gun': '/sounds/machine_gun.mp3',
    'machinegun': '/sounds/machine_gun.mp3', // Alias without space
    'monster': '/sounds/monster.mp3', // orders_units.attack_sound_type = 'Monster'
    'pistol': '/sounds/pistol.mp3',
    'reposition': '/sounds/reposition.mp3',
    'shadow': '/sounds/shadow.mp3',
    'shockwave': '/sounds/shockwave.mp3',
    'shotgun': '/sounds/shotgun.mp3',
    'shrink': '/sounds/shrink.mp3',
    'smoke': '/sounds/smoke.mp3',
    'sniper': '/sounds/shot.mp3',
    'sparks': '/sounds/spark.mp3',
    'spray': '/sounds/spray.mp3',
    'sword': '/sounds/sword.mp3',
    // Legacy mappings (for backwards compatibility)
    'fist': '/sounds/blunt.mp3',
    'sniper_shot': '/sounds/shot.mp3',
    'energy_weapon': '/sounds/energy.mp3',
    'fist_hit': '/sounds/blunt.mp3',
    'sword_slash': '/sounds/sword.mp3',
    'claw_attack': '/sounds/claw.mp3'
  };
  
  return soundPaths[normalizedType] || soundPaths['default'];
}

/**
 * Infer attack sound type from unit name (fallback method)
 * @param {string} name - Unit name
 * @returns {string|null} - Inferred sound type or null
 */
function inferSoundTypeFromName(name) {
  if (!name) return null;
  
  const nameLower = name.toLowerCase();
  
  // Sniper patterns
  if (nameLower.includes('sniper') || nameLower.includes('marksman') || nameLower.includes('rifle')) {
    return 'sniper';
  }
  
  // Energy weapon patterns
  if (nameLower.includes('laser') || nameLower.includes('plasma') || nameLower.includes('energy') || nameLower.includes('beam')) {
    return 'energy';
  }
  
  // Sword patterns
  if (nameLower.includes('sword') || nameLower.includes('blade') || nameLower.includes('katana') || nameLower.includes('saber')) {
    return 'sword';
  }
  
  // Claw patterns
  if (nameLower.includes('claw') || nameLower.includes('talon') || nameLower.includes('fang')) {
    return 'claws';
  }
  
  // Cannon patterns
  if (nameLower.includes('cannon') || nameLower.includes('artillery')) {
    return 'cannon';
  }
  
  // Machine gun patterns
  if (nameLower.includes('machine gun') || nameLower.includes('machinegun') || nameLower.includes('automatic')) {
    return 'machine gun';
  }
  
  // Pistol patterns
  if (nameLower.includes('pistol') || nameLower.includes('handgun')) {
    return 'pistol';
  }
  
  // Shotgun patterns
  if (nameLower.includes('shotgun')) {
    return 'shotgun';
  }
  
  // Explosive patterns
  if (nameLower.includes('explosive') || nameLower.includes('bomb') || nameLower.includes('grenade')) {
    return 'explosives';
  }
  
  // Acid patterns
  if (nameLower.includes('acid') || nameLower.includes('toxin') || nameLower.includes('venom')) {
    return 'acid';
  }
  
  // Fire patterns
  if (nameLower.includes('fire') || nameLower.includes('flame') || nameLower.includes('burn')) {
    return 'fire';
  }
  
  // Default to blunt for melee/brawler units
  if (nameLower.includes('fist') || nameLower.includes('punch') || nameLower.includes('brawler') || nameLower.includes('melee')) {
    return 'blunt';
  }
  
  return null;
}

/**
 * Alternative: Get attack sound based on unit name patterns
 * Useful if you want to infer sound type from name without hardcoding IDs
 * @param {Object} unit - Unit object with name property
 * @returns {string} - Path to attack sound file
 */
export function getAttackSoundByName(unit) {
  if (!unit || !unit.name) {
    return '/sounds/blunt.mp3';
  }
  
  const inferredType = inferSoundTypeFromName(unit.name);
  if (inferredType) {
    return getSoundPathForType(inferredType);
  }
  
  // Default
  return '/sounds/blunt.mp3';
}

